import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Save, Loader2, FolderOpen, Palette, Type, Music, Share2, Search as SearchIcon,
  ExternalLink, AlertCircle, Building2, ImageIcon
} from "lucide-react";
import MediaPickerModal from "../../components/MediaPickerModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FIELD = "block w-full bg-white border border-[#D4859B]/20 focus:border-seikat-pink rounded-xl px-3.5 py-2 text-[#1C1A19] placeholder:text-[#A8A29A] font-body text-sm outline-none transition-colors";
const LABEL = "block text-[10px] tracking-[0.2em] uppercase text-[#8A857D] mb-1.5 font-body";

const TABS = [
  { id: "brand", label: "Brand", icon: Building2 },
  { id: "theme", label: "Tema & Warna", icon: Palette },
  { id: "typo", label: "Tipografi", icon: Type },
  { id: "audio", label: "Audio Ambient", icon: Music },
  { id: "social", label: "Social & Kontak", icon: Share2 },
  { id: "seo", label: "SEO", icon: SearchIcon },
];

const FONT_PRESETS = [
  "Cormorant Garamond", "Playfair Display", "DM Serif Display", "EB Garamond",
  "Outfit", "DM Sans", "Inter", "Manrope", "Geist"
];

const COLOR_PRESETS = [
  { name: "Seikat Pink", color: "#D4859B" },
  { name: "Lime", color: "#C5DB7B" },
  { name: "Deep Espresso", color: "#120E0D" },
  { name: "Bone", color: "#F5F2EB" },
  { name: "Sand", color: "#E8DDD0" },
  { name: "Rose", color: "#E8B4B8" },
  { name: "Sage", color: "#9CAF88" },
  { name: "Indigo Deep", color: "#1C1A2E" },
];

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("brand");
  const [picker, setPicker] = useState(null); // { field, accept }

  useEffect(() => {
    axios.get(`${API}/cms/site-settings`).then(r => { setS(r.data); setInitial(JSON.stringify(r.data)); }).catch(() => toast.error("Gagal memuat settings"));
  }, []);

  const isDirty = useMemo(() => s && initial && JSON.stringify(s) !== initial, [s, initial]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/cms/site-settings`, s, { withCredentials: true });
      setInitial(JSON.stringify(s));
      toast.success("Settings tersimpan");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Simpan gagal");
    } finally {
      setSaving(false);
    }
  };

  const upd = (path, value) => {
    const parts = path.split(".");
    setS(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      let cur = n;
      for (let i = 0; i < parts.length - 1; i++) { cur[parts[i]] = cur[parts[i]] || {}; cur = cur[parts[i]]; }
      cur[parts[parts.length - 1]] = value;
      return n;
    });
  };

  const openPicker = (field, accept = "image") => setPicker({ field, accept });
  const handlePick = (url) => {
    if (!picker) return;
    const absolute = url.startsWith("http") ? url : `${BACKEND}${url}`;
    upd(picker.field, absolute);
  };

  if (!s) return <Loader2 className="animate-spin text-seikat-pink mx-auto mt-20" size={28} />;

  return (
    <div data-testid="admin-settings">
      {/* Header */}
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2"><span className="w-8 h-px bg-seikat-pink" /><span className="text-[10px] tracking-[0.35em] uppercase text-seikat-pink font-body">Site Settings</span></div>
          <h1 className="font-heading text-4xl text-[#1C1A19] leading-tight">Site <em className="text-seikat-pink">Settings</em></h1>
          <p className="font-body text-sm text-[#4A453F]/75 mt-1.5">Identitas brand, tema, font, audio, dan SEO.</p>
        </div>
        <div className="flex items-center gap-2">
          {isDirty && <span className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full text-[11px] font-body text-amber-700"><AlertCircle size={11} /> Belum disimpan</span>}
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-4 py-2 border border-[#D4859B]/20 rounded-full text-xs font-body text-[#4A453F] hover:bg-[#FDF8F6]"><ExternalLink size={11} /> Preview Site</a>
          <button onClick={save} disabled={saving || !isDirty} className="flex items-center gap-2 px-5 py-2.5 bg-seikat-pink text-white rounded-full text-sm font-body font-medium hover:bg-seikat-pink/90 transition-colors disabled:opacity-40" data-testid="admin-settings-save">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} {saving ? "Menyimpan…" : "Simpan"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border border-[#D4859B]/15 rounded-2xl p-1.5 mb-6 flex items-center gap-1 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-body whitespace-nowrap transition-colors ${
                tab === t.id ? "bg-seikat-pink text-white shadow-sm" : "text-[#4A453F] hover:bg-[#FDF8F6]"
              }`}
            >
              <Icon size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* BRAND */}
      {tab === "brand" && (
        <div className="bg-white border border-[#D4859B]/15 rounded-2xl p-5 mb-6">
          <div className="grid lg:grid-cols-2 gap-4">
            <div><label className={LABEL}>Brand Name</label><input className={FIELD} value={s.brand_name || ""} onChange={e => upd("brand_name", e.target.value)} data-testid="settings-brand-name" /></div>
            <div>
              <label className={LABEL}>Logo URL</label>
              <div className="flex gap-2">
                <input className={`${FIELD} flex-1`} value={s.logo_url || ""} onChange={e => upd("logo_url", e.target.value)} placeholder="https://…" />
                <button onClick={() => openPicker("logo_url", "image")} className="flex items-center gap-1.5 px-3 py-2 bg-seikat-pink/10 hover:bg-seikat-pink/20 text-seikat-pink rounded-xl text-xs font-body whitespace-nowrap" data-testid="logo-picker"><FolderOpen size={11} /> Galeri</button>
              </div>
              {s.logo_url && <img src={s.logo_url} alt="logo" className="mt-2 h-10 object-contain bg-[#FDF8F6] rounded-lg px-3 py-1.5" />}
            </div>
            <div>
              <label className={LABEL}>Favicon URL (32×32)</label>
              <div className="flex gap-2">
                <input className={`${FIELD} flex-1`} value={s.favicon_url || ""} onChange={e => upd("favicon_url", e.target.value)} placeholder="https://…" />
                <button onClick={() => openPicker("favicon_url", "image")} className="flex items-center gap-1.5 px-3 py-2 bg-seikat-pink/10 hover:bg-seikat-pink/20 text-seikat-pink rounded-xl text-xs font-body whitespace-nowrap" data-testid="favicon-picker"><FolderOpen size={11} /> Galeri</button>
              </div>
              {s.favicon_url && <img src={s.favicon_url} alt="favicon" className="mt-2 w-8 h-8 object-contain bg-[#FDF8F6] rounded-md p-1" />}
            </div>
            <div></div>
            <div><label className={LABEL}>🇮🇩 Tagline — Indonesia</label><input className={FIELD} value={s.tagline_id || ""} onChange={e => upd("tagline_id", e.target.value)} /></div>
            <div><label className={LABEL}>🇬🇧 Tagline — English</label><input className={FIELD} value={s.tagline_en || ""} onChange={e => upd("tagline_en", e.target.value)} /></div>
          </div>
        </div>
      )}

      {/* THEME */}
      {tab === "theme" && (
        <div className="space-y-6">
          {/* Live preview */}
          <div
            className="rounded-2xl p-6 border"
            style={{ backgroundColor: s.dark_color || "#120E0D", borderColor: `${s.primary_color || "#D4859B"}30` }}
          >
            <p className="text-xs tracking-widest uppercase mb-2" style={{ color: s.primary_color || "#D4859B" }}>LIVE PREVIEW</p>
            <p className="font-heading text-3xl mb-1" style={{ color: s.light_color || "#F5F2EB", fontFamily: s.heading_font }}>{s.brand_name || "SEIKAT"} <em style={{ color: s.primary_color || "#D4859B" }}>atelier</em></p>
            <p className="text-sm" style={{ color: `${s.light_color || "#F5F2EB"}aa`, fontFamily: s.body_font }}>{s.tagline_id || "Merangkai puisi alam, dengan tangan dan hati."}</p>
            <div className="flex gap-2 mt-3">
              <button className="px-4 py-1.5 rounded-full text-xs font-medium" style={{ backgroundColor: s.primary_color || "#D4859B", color: s.light_color || "#F5F2EB" }}>Primary CTA</button>
              <button className="px-4 py-1.5 rounded-full text-xs font-medium border" style={{ borderColor: s.accent_color || "#C5DB7B", color: s.accent_color || "#C5DB7B" }}>Accent</button>
            </div>
          </div>
          {/* Color editors */}
          <div className="bg-white border border-[#D4859B]/15 rounded-2xl p-5">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                ["primary_color", "Primary (accent 1)"],
                ["accent_color", "Accent 2"],
                ["dark_color", "Dark Base"],
                ["light_color", "Light Base"],
              ].map(([key, label]) => (
                <div key={key}>
                  <label className={LABEL}>{label}</label>
                  <div className="flex items-center gap-2">
                    <input type="color" className="w-12 h-10 rounded cursor-pointer bg-transparent border border-[#D4859B]/20" value={s[key] || "#000000"} onChange={e => upd(key, e.target.value)} />
                    <input className={FIELD} value={s[key] || ""} onChange={e => upd(key, e.target.value)} data-testid={`settings-${key.replace(/_/g, "-")}`} />
                  </div>
                </div>
              ))}
            </div>
            <p className={LABEL + " mt-5"}>Preset Warna (klik untuk apply ke Primary)</p>
            <div className="flex flex-wrap gap-2">
              {COLOR_PRESETS.map(p => (
                <button key={p.color} onClick={() => upd("primary_color", p.color)} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-[#FDF8F6] border border-[#D4859B]/15 hover:border-seikat-pink text-[11px] font-body text-[#4A453F]">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }} />
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TYPOGRAPHY */}
      {tab === "typo" && (
        <div className="bg-white border border-[#D4859B]/15 rounded-2xl p-5">
          <div className="grid lg:grid-cols-2 gap-4">
            <div>
              <label className={LABEL}>Heading Font</label>
              <input list="heading-fonts" className={FIELD} value={s.heading_font || ""} onChange={e => upd("heading_font", e.target.value)} placeholder="Cormorant Garamond" />
              <datalist id="heading-fonts">{FONT_PRESETS.map(f => <option key={f} value={f} />)}</datalist>
            </div>
            <div>
              <label className={LABEL}>Body Font</label>
              <input list="body-fonts" className={FIELD} value={s.body_font || ""} onChange={e => upd("body_font", e.target.value)} placeholder="Outfit" />
              <datalist id="body-fonts">{FONT_PRESETS.map(f => <option key={f} value={f} />)}</datalist>
            </div>
          </div>
          <div className="mt-5 p-5 rounded-xl bg-[#FDF8F6] border border-[#D4859B]/10">
            <p className="text-[10px] tracking-widest uppercase text-[#8A857D] mb-2">Preview</p>
            <p style={{ fontFamily: s.heading_font }} className="text-3xl text-[#1C1A19] leading-tight">Sebuah <em className="text-seikat-pink">atelier bunga</em>, dirangkai dengan hati.</p>
            <p style={{ fontFamily: s.body_font }} className="text-sm text-[#4A453F] mt-2">Di Seikat, setiap tangkai adalah sebait puisi — dipilih dengan tenang, dirangkai dengan hangat.</p>
          </div>
        </div>
      )}

      {/* AUDIO */}
      {tab === "audio" && (
        <div className="bg-white border border-[#D4859B]/15 rounded-2xl p-5">
          <div>
            <label className={LABEL}>Audio URL (MP3)</label>
            <div className="flex gap-2">
              <input className={`${FIELD} flex-1`} value={s.audio_url || ""} onChange={e => upd("audio_url", e.target.value)} placeholder="https://cdn…" data-testid="settings-audio-url" />
              <button onClick={() => openPicker("audio_url", "audio")} className="flex items-center gap-1.5 px-3 py-2 bg-seikat-pink/10 hover:bg-seikat-pink/20 text-seikat-pink rounded-xl text-xs font-body whitespace-nowrap" data-testid="audio-picker"><FolderOpen size={11} /> Galeri</button>
            </div>
            {s.audio_url && <audio controls src={s.audio_url} className="mt-3 w-full max-w-md" />}
            <p className="text-[11px] text-[#8A857D] mt-2 italic">Audio ambient akan diputar di halaman immersive saat user mengaktifkannya.</p>
          </div>
          <div className="mt-4">
            <label className="flex items-center gap-2 text-sm font-body text-[#1C1A19] cursor-pointer">
              <input type="checkbox" checked={!!s.audio_enabled_default} onChange={e => upd("audio_enabled_default", e.target.checked)} className="w-4 h-4 accent-seikat-pink" />
              Aktifkan audio secara default saat user pertama kunjung
            </label>
          </div>
        </div>
      )}

      {/* SOCIAL & CONTACT */}
      {tab === "social" && (
        <div className="bg-white border border-[#D4859B]/15 rounded-2xl p-5">
          <div className="grid lg:grid-cols-2 gap-4">
            <div><label className={LABEL}>Instagram</label><input className={FIELD} value={s.social?.instagram || ""} onChange={e => upd("social.instagram", e.target.value)} placeholder="https://instagram.com/…" /></div>
            <div><label className={LABEL}>TikTok</label><input className={FIELD} value={s.social?.tiktok || ""} onChange={e => upd("social.tiktok", e.target.value)} /></div>
            <div><label className={LABEL}>WhatsApp</label><input className={FIELD} value={s.social?.whatsapp || ""} onChange={e => upd("social.whatsapp", e.target.value)} placeholder="+62…" /></div>
            <div><label className={LABEL}>Email</label><input className={FIELD} value={s.social?.email || ""} onChange={e => upd("social.email", e.target.value)} /></div>
            <div><label className={LABEL}>Alamat</label><input className={FIELD} value={s.contact?.address || ""} onChange={e => upd("contact.address", e.target.value)} /></div>
            <div><label className={LABEL}>Telepon</label><input className={FIELD} value={s.contact?.phone || ""} onChange={e => upd("contact.phone", e.target.value)} /></div>
            <div className="lg:col-span-2"><label className={LABEL}>Jam Operasional</label><input className={FIELD} value={s.contact?.hours || ""} onChange={e => upd("contact.hours", e.target.value)} placeholder="Senin–Sabtu · 09.00–20.00 WIB" /></div>
          </div>
        </div>
      )}

      {/* SEO */}
      {tab === "seo" && (
        <div className="bg-white border border-[#D4859B]/15 rounded-2xl p-5">
          <div className="grid lg:grid-cols-2 gap-4">
            <div><label className={LABEL}>Page Title</label><input className={FIELD} value={s.seo?.title || ""} onChange={e => upd("seo.title", e.target.value)} /></div>
            <div>
              <label className={LABEL}>OG Image URL (Social preview, 1200×630 ideal)</label>
              <div className="flex gap-2">
                <input className={`${FIELD} flex-1`} value={s.seo?.og_image || ""} onChange={e => upd("seo.og_image", e.target.value)} />
                <button onClick={() => openPicker("seo.og_image", "image")} className="flex items-center gap-1.5 px-3 py-2 bg-seikat-pink/10 hover:bg-seikat-pink/20 text-seikat-pink rounded-xl text-xs font-body whitespace-nowrap" data-testid="og-picker"><FolderOpen size={11} /> Galeri</button>
              </div>
            </div>
            <div className="lg:col-span-2"><label className={LABEL}>Meta Description</label><textarea rows={3} className={FIELD} value={s.seo?.description || ""} onChange={e => upd("seo.description", e.target.value)} /></div>
          </div>
          {s.seo?.og_image && (
            <div className="mt-5 p-4 rounded-xl bg-[#FDF8F6] border border-[#D4859B]/15">
              <p className="text-[10px] tracking-widest uppercase text-[#8A857D] mb-2">Preview Social Card</p>
              <div className="max-w-md rounded-lg overflow-hidden bg-white border border-[#D4859B]/10">
                <img src={s.seo.og_image} alt="OG" className="w-full h-40 object-cover" />
                <div className="p-3">
                  <p className="font-heading text-base text-[#1C1A19] truncate">{s.seo?.title || s.brand_name}</p>
                  <p className="font-body text-xs text-[#4A453F] line-clamp-2 mt-1">{s.seo?.description}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <MediaPickerModal
        open={!!picker}
        onClose={() => setPicker(null)}
        onSelect={handlePick}
        accept={picker?.accept || "image"}
      />
    </div>
  );
}
